/**
 * Relacion a un registro (`many2one`).
 *
 * Con valor elegido no se muestra un input con texto adentro sino el registro ya
 * resuelto, con dos acciones separadas: abrirlo y quitarlo. Es lo que evita el
 * error mas comun de los ERPs — borrar media palabra del nombre sin querer y
 * dejar el campo apuntando a otra cosa.
 *
 * Lo unico que este componente hace con `options.quick_create` es PASARLO: la
 * decision de si se puede crear al vuelo, y como, vive entera en el
 * `RelationPicker`, que es quien tiene la metadata del modelo destino y sus
 * permisos. Aca no hay logica de negocio ni un nombre de modelo escrito.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { isMany2One } from '@/components/fields/format';
import { leerQuickCreate, RelationPicker } from '@/components/fields/RelationPicker';
import type { FieldInputProps } from '@/components/fields/types';
import { IconChevronRight, IconClose } from '@/components/shell/icons';
import { recordPath } from '@/components/shell/menu-utils';
import type { Many2OneValue } from '@/lib/types';

export function Many2OneField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  const [editando, setEditando] = useState(false);
  const seleccionado: Many2OneValue | null = isMany2One(value) ? value : null;

  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  if (meta.relation === undefined) {
    // Metadata incompleta del backend: se dice, no se dibuja un control mudo.
    return (
      <p id={id} className="text-sm text-danger">
        El campo no declara a qué modelo apunta.
      </p>
    );
  }

  if (seleccionado !== null && !editando) {
    return (
      <div className="flex items-stretch gap-1.5">
        <button
          id={id}
          type="button"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onClick={() => {
            setEditando(true);
          }}
          className="touch-target flex min-w-0 flex-1 items-center rounded-md border border-line bg-surface px-3 py-2 text-left text-sm transition-colors hover:border-line-strong sm:min-h-0 sm:h-9 sm:py-1.5"
        >
          <span className="truncate">{seleccionado.display_name}</span>
        </button>

        <Link
          to={recordPath(meta.relation, seleccionado.id)}
          className="grid w-9 shrink-0 place-items-center rounded-md border border-line text-fg-muted transition-colors hover:border-line-strong hover:text-fg"
          aria-label={`Abrir ${seleccionado.display_name}`}
          title="Abrir"
        >
          <IconChevronRight className="h-4 w-4" />
        </Link>

        {!required && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
            }}
            className="grid w-9 shrink-0 place-items-center rounded-md border border-line text-fg-muted transition-colors hover:border-danger hover:text-danger"
            aria-label={`Quitar ${seleccionado.display_name}`}
            title="Quitar"
          >
            <IconClose className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-stretch gap-1.5">
      <div className="min-w-0 flex-1">
        <RelationPicker
          id={id}
          relation={meta.relation}
          required={required}
          invalid={invalid}
          autoFocus={editando}
          placeholder={`Buscar ${meta.label.toLowerCase()}…`}
          quickCreate={leerQuickCreate(meta.options)}
          {...(describedBy === undefined ? {} : { describedBy })}
          onPick={(registro) => {
            onChange(registro);
            setEditando(false);
          }}
        />
      </div>

      {/* Salida sin cambios: cambiar de idea no puede costar volver a buscar el
          registro que ya estaba elegido. */}
      {editando && seleccionado !== null && (
        <button
          type="button"
          onClick={() => {
            setEditando(false);
          }}
          className="grid w-9 shrink-0 place-items-center rounded-md border border-line text-fg-muted transition-colors hover:border-line-strong hover:text-fg"
          aria-label="Cancelar el cambio"
          title="Cancelar"
        >
          <IconClose className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
